import { useQuery } from '@apollo/client/react';
import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material';

import { gql } from '@graphql/__generated__';
import type { GetDocumentTypesQuery } from '@graphql/__generated__/graphql';
import { useLocale } from '@shared/hooks/locale';

type DocumentType = GetDocumentTypesQuery['document_types'][number];

export const GET_DOCUMENT_TYPES = gql(/* GraphQL */ `
  query GetDocumentTypes($types: [String!]!) {
    document_types: dictionaries(
      where: { type: { _eq: documents }, types: { _contains: $types } }
      order_by: { label: asc }
    ) {
      id
      name
      label
    }
  }
`);

export const FileTypeAutocomplete: React.FC<
  {
    value?: DocumentType;
    types: string[];
    placeholder?: string;
  } & Pick<
    AutocompleteProps<DocumentType, false, true, false>,
    'onChange' | 'disabled'
  >
> = ({ onChange, value, types, disabled = false, placeholder }) => {
  const { t } = useLocale();
  const { data, error, loading } = useQuery(GET_DOCUMENT_TYPES, {
    variables: { types },
  });

  const documentTypes = data?.document_types ?? [];

  return (
    <Autocomplete
      fullWidth
      autoComplete={false}
      autoCorrect="off"
      options={documentTypes}
      onChange={onChange}
      disableClearable
      disabled={disabled}
      value={value}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      slotProps={{
        paper: {
          elevation: 3,
          sx: {
            borderRadius: '4px',
            width: 300,
          },
        },
      }}
      loading={loading}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          variant="outlined"
          sx={{
            background: 'var(--mui-palette-background-paper)',
            '& .MuiInputBase-root': {
              py: '10px !important',
            },
          }}
          slotProps={{
            ...params.slotProps,

            htmlInput: {
              ...params.slotProps.htmlInput,
              autoComplete: 'one-time-code', // disable autocomplete and autofill
              type: 'new-password',
              name: 'new-password',
              autoCorrect: 'off',
              spellCheck: 'false',
            },
          }}
          placeholder={placeholder ?? t('Not specified')}
          error={error != null}
          helperText={
            error != null ? t('Error loading document types') : undefined
          }
        />
      )}
    />
  );
};
